import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { getStringDate } from 'src/app/common/util';
import { CoaResponse } from 'src/app/models/coa-parameter';
import { SimplePaginationModel } from 'src/app/models/pagination.model';
import { ResponseObject } from 'src/app/models/response-object.model';
import { AssociationExitNoteCoaService } from 'src/app/services/association-exit-note-coa.service';
import { AssociationExitNoteService } from 'src/app/services/association-exit-note.service';
import { CoasService } from 'src/app/services/coas.service';
import { ExitNoteService } from 'src/app/services/exit-note.service';

type ExitNoteVM = {
  id: number;
  number: string;
  providerName: string;
  date: string;
};

@Component({
  selector: 'app-association-exit-note-coa',
  templateUrl: './association-exit-note-coa.component.html',
  styleUrls: ['./association-exit-note-coa.component.scss']
})
export class AssociationExitNoteCoaComponent {
  loading = false;
  
    allExitNotesNotAssociated: ExitNoteVM[] = [];
    allExitNotesAssociated: ExitNoteVM[] = [];
  
    exitNotesNotAssociated: ExitNoteVM[] = [];
    exitNotesAssociated: ExitNoteVM[] = [];
  
    allCoas: CoaResponse[] = [];
    coas: CoaResponse[] = [];
  
    filterExitNotes = '';
    filterCoas = '';
  
    selectedExitNoteId: number | null = null;
    selectedExitNote: ExitNoteVM | null = null;
  
    initialAssociatedCoaIds: number[] = [];
    currentAssociatedCoaIds: number[] = [];
    currentAssociatedCoas: CoaResponse[] = [];
  
    startDate = '';
    endDate = '';
  
    paginationNotAssociated = new SimplePaginationModel(0, 5, 0, 0, 0);
    paginationAssociated = new SimplePaginationModel(0, 5, 0, 0, 0);
    paginationCoas = new SimplePaginationModel(0, 5, 0, 0, 0);
  
    coaDetails: any[] = [];
    loadingCoaDetails = false;
  
    exitNoteDetails: any[] = [];
    loadingExitNoteDetails = false;
  
  
    @ViewChild('svgContainer', { static: true }) svgContainer!: ElementRef;
    @HostListener('window:resize') onResize() { this.drawLinks(); }
  
    private timeoutExitNotes: any = null;
    private timeoutCoas: any = null;
  
    constructor(
      private toastService: ToastrService,
      private exitNoteService: ExitNoteService,
      private coasService: CoasService,
      private associationService: AssociationExitNoteCoaService
    ) {
      const today = new Date();
      this.startDate = getStringDate(today);
      this.endDate = getStringDate(today);
    }
  
    ngOnInit(): void {
      this.loadStartData();
    }
  
    loadStartData() {
      this.loadExitNotesNotAssociated();
      this.loadExitNotesAssociated();
      this.loadCoas();
    }
  
    loadExitNotesNotAssociated() {
      this.exitNoteService.getExitNotesAllNotAssociated().subscribe({
        next: (resp: ResponseObject) => {
          this.allExitNotesNotAssociated = (resp.data || []).map((en: any) => this.normalizeExitNote(en));
          console.log(this.allExitNotesNotAssociated);
          
          this.refreshNotAssociatedPagination();
        },
        error: () => this.toastService.error('Error al obtener notas no asociadas')
      });
    }
  
    loadExitNotesAssociated() {
      this.exitNoteService.getExitNotesAllAssociated().subscribe({
        next: (resp: ResponseObject) => {
          this.allExitNotesAssociated = (resp.data || []).map((en: any) => this.normalizeExitNote(en));
          this.refreshAssociatedPagination();
        },
        error: () => this.toastService.error('Error al obtener notas asociadas')
      });
    }
  
    loadCoas() {
      this.coasService.getCoas().subscribe({
        next: (resp: ResponseObject) => {
          this.allCoas = resp.data || [];
          this.allCoas.sort((a, b) => Number(b.number) - Number(a.number));
          this.refreshCoasPagination();
        },
        error: () => this.toastService.error('Error al obtener COAs')
      });
    }
  
    private normalizeExitNote(en: any): ExitNoteVM {
      return {
        id: en.id,
        number: String(en.noteNumber),
        providerName: en.providerData?.name ?? '—',
        date: new Date(en.exitDate).toLocaleDateString()
      };
    }
  
    selectExitNote(exitNoteId: number) {
      if (this.selectedExitNoteId === exitNoteId) return;
  
      this.selectedExitNoteId = exitNoteId;
      this.selectedExitNote =
        this.allExitNotesNotAssociated.find(e => e.id === exitNoteId) ||
        this.allExitNotesAssociated.find(e => e.id === exitNoteId) || null;
  
      this.currentAssociatedCoaIds = [];
      this.currentAssociatedCoas = [];
      this.coaDetails = []; // Limpia el detalle previo
  
      this.associationService.getAssociationsByExitNote(exitNoteId).subscribe({
        next: (resp: ResponseObject) => {
          const associations = resp.data || [];
          const relatedIds = associations.map((a: any) => a.coaId);
  
          this.initialAssociatedCoaIds = [...relatedIds];
          this.currentAssociatedCoaIds = [...relatedIds];
          this.currentAssociatedCoas = this.allCoas.filter(c => relatedIds.includes(c.id));
  
          this.refreshCoasPagination();
          this.drawLinks();
  
          if (this.currentAssociatedCoaIds.length > 0) {
            const firstCoaId = this.currentAssociatedCoaIds[0];
            this.getCoasDetailByIdCoa(firstCoaId);
          }
  
          this.getExitNoteDetails(exitNoteId);
        },
        error: () => this.toastService.error('Error al obtener asociaciones de la nota de salida')
      });
    }
  
    getExitNoteDetails(exitNoteId: number) {
      this.loadingExitNoteDetails = true;
      this.exitNoteService.getExitNoteById(exitNoteId).subscribe({
        next: (resp: ResponseObject) => {
          this.exitNoteDetails = resp.data?.details || [];
          this.loadingExitNoteDetails = false;
        },
        error: () => {
          this.toastService.error('Error al obtener detalles de la nota de salida');
          this.loadingExitNoteDetails = false;
        }
      });
    }
  
  
  
    toggleCoa(coa: CoaResponse) {
      if (!this.selectedExitNoteId) {
        this.toastService.warning('Selecciona primero una nota de salida.');
        return;
      }
  
      const isSelected = this.currentAssociatedCoaIds.includes(coa.id);
      if (!isSelected && this.currentAssociatedCoaIds.length >= 1) {
        this.toastService.warning('Solo se permite asociar un COA por nota de salida.');
        return;
      }
  
      this.currentAssociatedCoaIds = isSelected ? [] : [coa.id];
      this.currentAssociatedCoas = this.allCoas.filter(c => this.currentAssociatedCoaIds.includes(c.id));
      this.refreshCoasPagination();
      this.drawLinks();
  
      if (!isSelected) {
        this.getCoasDetailByIdCoa(coa.id);
      } else {
        this.coaDetails = [];
      }
    }
  
  
    getDisabledSave(): boolean {
      const initialSet = new Set(this.initialAssociatedCoaIds);
      const currentSet = new Set(this.currentAssociatedCoaIds);
      const toAdd = this.currentAssociatedCoaIds.some(id => !initialSet.has(id));
      const toRemove = this.initialAssociatedCoaIds.some(id => !currentSet.has(id));
      return !(toAdd || toRemove) || !this.selectedExitNoteId || this.loading;
    }
  
    saveAssociations() {
      if (!this.selectedExitNoteId) return;
      const exitNoteId = this.selectedExitNoteId;
      const initialSet = new Set(this.initialAssociatedCoaIds);
      const currentSet = new Set(this.currentAssociatedCoaIds);
      const toAdd = this.currentAssociatedCoaIds.filter(id => !initialSet.has(id));
      const toRemove = this.initialAssociatedCoaIds.filter(id => !currentSet.has(id));
  
      if (toAdd.length === 0 && toRemove.length === 0) {
        this.toastService.info('No hay cambios por guardar.');
        return;
      }
  
      this.loading = true;
      const deleteOps = toRemove.map(coaId => this.associationService.deleteAssociation(0, exitNoteId, coaId));
      const createOps = toAdd.map(coaId => this.associationService.createAssociation(exitNoteId, coaId));
      const deleteStep = deleteOps.length > 0 ? forkJoin(deleteOps) : of(null);
  
      deleteStep.pipe(
        switchMap(() => createOps.length > 0 ? forkJoin(createOps) : of(null))
      ).subscribe({
        next: () => {
          this.toastService.success('Asociaciones actualizadas correctamente', 'Éxito');
          this.loading = false;
          this.loadStartData();
          this.selectExitNote(exitNoteId);
        },
        error: (e: any) => {
          console.error(e);
          this.toastService.error(e?.error?.message || 'Error al actualizar asociaciones', 'Error');
          this.loading = false;
        }
      });
    }
  
    refreshNotAssociatedPagination() {
      const q = this.filterExitNotes.toLowerCase();
      const filtered = this.allExitNotesNotAssociated.filter(e =>
        e.number.toLowerCase().includes(q) || e.providerName.toLowerCase().includes(q)
      );
      this.paginationNotAssociated.total = filtered.length;
      this.exitNotesNotAssociated = filtered.slice(
        this.paginationNotAssociated.page * this.paginationNotAssociated.size,
        (this.paginationNotAssociated.page + 1) * this.paginationNotAssociated.size
      );
    }
  
    refreshAssociatedPagination() {
      const q = this.filterExitNotes.toLowerCase();
      const filtered = this.allExitNotesAssociated.filter(e =>
        e.number.toLowerCase().includes(q) || e.providerName.toLowerCase().includes(q)
      );
      this.paginationAssociated.total = filtered.length;
      this.exitNotesAssociated = filtered.slice(
        this.paginationAssociated.page * this.paginationAssociated.size,
        (this.paginationAssociated.page + 1) * this.paginationAssociated.size
      );
    }
  
    refreshCoasPagination() {
      const q = (this.filterCoas || '').toLowerCase();
      const matches = (c: CoaResponse) =>
        (c.code || '').toLowerCase().includes(q) ||
        (c.number + '').toLowerCase().includes(q) ||
        (c.productData?.name || '').toLowerCase().includes(q);
  
      const associatedSet = new Set(this.currentAssociatedCoaIds);
      const pinned = this.allCoas.filter(c => associatedSet.has(c.id));
      const rest = this.allCoas.filter(c => !associatedSet.has(c.id) && matches(c));
      const final = [...pinned, ...rest];
  
      this.paginationCoas.total = final.length;
      this.coas = final.slice(0, this.paginationCoas.size);
    }
  
    drawLinks() {
      const svg = this.svgContainer?.nativeElement as SVGElement;
      if (!svg) return;
  
      svg.innerHTML = ''; // limpiar líneas previas
  
      // Si no hay nota seleccionada o no hay COAs asociados, no dibujar nada
      if (!this.selectedExitNoteId || this.currentAssociatedCoaIds.length === 0) return;
  
      setTimeout(() => {
        const parentRect = (svg.parentElement as HTMLElement).getBoundingClientRect();
  
        // Buscar en ambas tablas la fila seleccionada
        const selectedRow =
          document.querySelector('.table tr.selected') as HTMLElement; // cualquier tabla de exit notes
        if (!selectedRow) return;
  
        const selectedRect = selectedRow.getBoundingClientRect();
  
        // Punto de partida (lado derecho de la fila de nota)
        const x1 = selectedRect.right - parentRect.left - 12;
        const y1 = selectedRect.top + selectedRect.height / 2 - parentRect.top;
  
        for (const id of this.currentAssociatedCoaIds) {
          const relatedRow = document.querySelector(`#table-coas tr[data-id="${id}"]`) as HTMLElement;
          if (!relatedRow) continue;
  
          const rect = relatedRow.getBoundingClientRect();
          const x2 = rect.left - parentRect.left - 12;
          const y2 = rect.top + rect.height / 2 - parentRect.top;
  
          const xMiddle = x1 + (x2 - x1) / 2;
  
          this.drawLine(svg, x1, xMiddle, y1, y1);
          this.drawLine(svg, xMiddle, x2, y2, y2);
          this.drawLine(svg, xMiddle, xMiddle, y1, y2);
        }
      }, 50);
    }
  
    private drawLine(svg: SVGElement, x1: number, x2: number, y1: number, y2: number) {
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', String(x1));
      line.setAttribute('y1', String(y1));
      line.setAttribute('x2', String(x2));
      line.setAttribute('y2', String(y2));
      line.setAttribute('stroke', '#001f54');
      line.setAttribute('stroke-width', '2');
      svg.appendChild(line);
    }
  
    debouncedFilterCoas() {
      clearTimeout(this.timeoutCoas);
      this.timeoutCoas = setTimeout(() => {
        this.paginationCoas.page = 0;
        this.refreshCoasPagination();
      }, 250);
    }
  
    getCoasDetailByIdCoa(coaId: number) {
      this.loadingCoaDetails = true;
      this.coasService.getCoasDetailById(coaId).subscribe({
        next: (resp: ResponseObject) => {
          this.coaDetails = resp.data || [];
          this.loadingCoaDetails = false;
        },
        error: () => {
          this.toastService.error('Error al obtener el detalle del COA');
          this.loadingCoaDetails = false;
        }
      });
    }
}
